import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnalyticsView, ChartKind } from './components';
import { AnalyticsDomainConfig } from './analyticsDomainConfig';
import { DrillDownContext } from './useAnalyticsExploration';

export interface AnalyticsUrlState {
  groupBy: string;
  formValues: Record<string, unknown>;
  view: AnalyticsView;
  chartKind: ChartKind;
  yKey: string;
  drillDownContext?: DrillDownContext;
  visibleColumns: string[];
}

interface UseAnalyticsSearchParamsResult {
  syncToUrl: (domain: AnalyticsDomainConfig, state: AnalyticsUrlState) => void;
  readFromUrl: (domain: AnalyticsDomainConfig) => AnalyticsUrlState | undefined;
}

const buildSearchString = (domain: AnalyticsDomainConfig, state: AnalyticsUrlState, currentSearch: string): string => {
  const params = new URLSearchParams();
  params.set('tab', 'explorer');

  if (state.groupBy !== domain.defaultGroupBy) {
    params.set('groupBy', state.groupBy);
  }
  if (state.view !== 'chart') {
    params.set('view', state.view);
  }
  if (state.chartKind !== 'bar') {
    params.set('chartKind', state.chartKind);
  }
  if (state.yKey !== 'count') {
    params.set('yKey', state.yKey);
  }

  const domainParams = domain.toUrlParams(state.formValues);
  for (const [key, value] of Object.entries(domainParams)) {
    params.set(key, value);
  }

  if (state.drillDownContext) {
    params.set('drill', JSON.stringify(state.drillDownContext));
  }
  if (state.visibleColumns.length > 0) {
    params.set('vc', state.visibleColumns.join(','));
  }

  // Preserve params not managed by the explorer (e.g. drawerId set by AnalyticsRowsTable).
  // urlParamKeys covers every key the domain may emit so cleared filters don't leak back in
  // from the previous URL — Object.keys(domainParams) would only list currently-set keys.
  const managed = new Set<string>(['tab', 'groupBy', 'view', 'chartKind', 'yKey', 'drill', 'vc', ...domain.urlParamKeys]);
  const current = new URLSearchParams(currentSearch);
  for (const [key, value] of current.entries()) {
    if (!managed.has(key)) params.set(key, value);
  }

  return `?${params.toString()}`;
};

export const useAnalyticsSearchParams = (): UseAnalyticsSearchParamsResult => {
  const navigate = useNavigate();
  const location = useLocation();
  // Marks a navigation queued in the current render but not yet committed to location.search.
  // Cleared whenever the router commits anything, so it can't veto a legitimate later write.
  const pendingWriteRef = useRef<string | null>(null);

  useEffect(() => {
    pendingWriteRef.current = null;
  }, [location.search]);

  const syncToUrl = useCallback(
    (domain: AnalyticsDomainConfig, state: AnalyticsUrlState) => {
      const newSearch = buildSearchString(domain, state, location.search);

      if (newSearch === location.search) return;
      if (newSearch === pendingWriteRef.current) return;

      pendingWriteRef.current = newSearch;
      navigate({ search: newSearch }, { replace: true });
    },
    [navigate, location.search],
  );

  const readFromUrl = useCallback(
    (domain: AnalyticsDomainConfig) => {
      const params = new URLSearchParams(location.search);
      if (params.get('tab') !== 'explorer') return undefined;

      const rawGroupBy = params.get('groupBy');
      const groupBy = rawGroupBy && domain.groupByOptions.some((o) => o.value === rawGroupBy) ? rawGroupBy : domain.defaultGroupBy;
      const rawView = params.get('view');
      const view: AnalyticsView = rawView === 'chart' || rawView === 'table' ? rawView : 'chart';
      const rawChartKind = params.get('chartKind');
      const chartKind: ChartKind = rawChartKind === 'bar' || rawChartKind === 'pie' ? rawChartKind : 'bar';
      // yKey is validated at render time against the current response's columns metadata,
      // so accept whatever is in the URL here (or fall back to 'count').
      const yKey = params.get('yKey') ?? 'count';
      const rawVc = params.get('vc');
      const visibleColumns = rawVc ? rawVc.split(',').filter(Boolean) : [];
      const formValues = domain.fromUrlParams(params);

      let drillDownContext: DrillDownContext | undefined;
      const rawDrill = params.get('drill');
      if (rawDrill) {
        try {
          const parsed = JSON.parse(rawDrill) as DrillDownContext;
          if (
            parsed &&
            typeof parsed.label === 'string' &&
            typeof parsed.previousGroupBy === 'string' &&
            typeof parsed.previousFormValues === 'object' &&
            parsed.previousFormValues !== null &&
            !Array.isArray(parsed.previousFormValues) &&
            (parsed.previousView === 'chart' || parsed.previousView === 'table')
          ) {
            drillDownContext = parsed;
          }
        } catch {
          // Malformed drill param — silently drop and continue without drill state.
        }
      }

      return { groupBy, formValues, view, chartKind, yKey, drillDownContext, visibleColumns };
    },
    [location.search],
  );

  return { syncToUrl, readFromUrl };
};
